'use client'

import { useAuth } from '@clerk/nextjs'
import { Button } from '@/components/ui/button'
import { AUTH_STEPS } from '@/features/auth/helpers/auth-constants'
import {
    hasLandingSignedInHint,
    useLandingAuthContext,
} from '@/features/landing-page/landing-auth-context'
import { useSignOut } from '@/hooks/use-sign-out'
import { UrlConfig } from '@/setup/routing/routing'
import { cn } from '@/utils/styles/cn'

interface INavCtaButtonsProps {
    /**
     * `row` — desktop bar (Login → Get started); `stack` — mobile sheet, full-width (Get started → Login);
     * `compact` — mobile bar, just the Get started button.
     */
    layout?: 'row' | 'stack' | 'compact'
    /** Called after a button is pressed — used to close the mobile sheet. */
    onNavigate?: () => void
}

/**
 * The nav CTA actions — auth-aware, mirroring the live landing nav: signed OUT shows
 * Login / Get started (auth modal), signed IN shows Sign out / Go to dashboard
 * (handleCtaClick already routes signed-in users to the dashboard). Client island via
 * LandingAuthContext; kept tiny so the rest of the nav stays a server component.
 */
export function NavCtaButtons({ layout = 'row', onNavigate }: INavCtaButtonsProps) {
    const { handleCtaClick, openAuthModal } = useLandingAuthContext()
    const { isSignedIn } = useAuth()
    const { signOut } = useSignOut({ redirectUrl: UrlConfig['/'].getUrl() })
    const stack = layout === 'stack'

    const primary = (
        <Button
            key="primary"
            variant="lp-primary"
            size="lp"
            className={cn(stack && 'w-full')}
            data-cta="get_started"
            data-section="nav"
            onClick={() => {
                handleCtaClick()
                onNavigate?.()
            }}
        >
            <span className="landing-auth-hint-signed-in">Go to dashboard</span>
            <span className="landing-auth-hint-signed-out">Get started</span>
        </Button>
    )

    const secondary = (
        <Button
            key="secondary"
            variant="lp-secondary"
            size="lp"
            className={cn(stack && 'w-full')}
            onClick={() => {
                if (isSignedIn || hasLandingSignedInHint()) signOut()
                else openAuthModal(AUTH_STEPS.SignIn)
                onNavigate?.()
            }}
        >
            <span className="landing-auth-hint-signed-in">Sign out</span>
            <span className="landing-auth-hint-signed-out">Login</span>
        </Button>
    )

    if (layout === 'compact') return primary

    return (
        <div className={cn(stack ? 'flex w-full flex-col gap-2' : 'flex items-center gap-2')}>
            {stack ? [primary, secondary] : [secondary, primary]}
        </div>
    )
}
